import { useMemo, useState } from "react";
import { faCubes, faDeleteLeft, faDoorOpen, faTableCells, faTrashCan, faTrowelBricks } from "@fortawesome/free-solid-svg-icons";
import { Icon } from "../../../lib/Icon";
import { tipAttrs } from "../../../lib/tooltip";
import { encounterGround, isMapAssetId, mapAsset, OPEN_GROUND_TILE, TERRAIN_TILES, type EncounterCommand, type EncounterState } from "@lancer-vtt/domain";
import { TilePalette } from "../../../lib/encounter/combatWindows/TilePalette";
import { nameGlyph } from "../combatCells";
import { HAZARD_TYPES, isMechanicBrush, type CombatBrush, type CoverGrade, type WallHeight } from "../combatAuthoring";
import { ASSET_PRESET_LIST, MAX_ARMOR, MIN_ARMOR, OBJECT_PRESET_LIST, OBJECT_PRESETS, listObjectGroups, pickDefaults, pickLabel, pickSectionRange, placedObjectRemoveCommand, presetHasHeight, type ObjectGroupSummary, type ObjectOrientation, type ObjectPick } from "../objectAuthoring";
import type { RailContext, RailTool } from "./BoardToolRail";
import type { AuthoringToolbar } from "./useAuthoringToolbar";

/**
 * **The rail's BUILD section** (`board-tool-rail` Phase 5, `decisions/0191` Ruling 5): the GM's
 * authoring tools, EDIT only. Wall (6), Room (7) and Objects (8) arm pointer tools; Tile (T) arms the
 * tile brush and opens the palette, whose ADVANCED fold holds the custom-cell brushes on 2–5 and 9;
 * X flips the erase direction. (U, which opened the UNITS window, went when UNITS became a page,
 * `website-shell` 4.1.) An armed tool's options open in a panel
 * beside the rail and close with it. Arming no longer depends on a window being open: only Esc, a rail
 * press or arming something else disarms.
 */

/** What the BUILD tools read and change. `CombatApp` builds it from the authoring toolbar. */
export type BuildContext = {
  toolbar: AuthoringToolbar;
  state: EncounterState;
  actorId: string;
  /** The tools are inert (disconnected or no identity). */
  disabled: boolean;
  dispatch: (command: EncounterCommand) => unknown;
};

/** The custom-cell brushes in the palette's ADVANCED fold, on the number keys `decisions/0039` gave them (kept by `decisions/0191`). */
const ADVANCED_BRUSHES: { id: Exclude<CombatBrush, "tile" | "erase">; label: string; key: string }[] = [
  { id: "solid", label: "Solid", key: "2" },
  { id: "cover", label: "Cover", key: "3" },
  { id: "difficult", label: "Difficult", key: "4" },
  { id: "dangerous", label: "Dangerous", key: "5" },
  { id: "pit", label: "Pit", key: "9" },
];

/** A pick's `objectProfile` flags, as a short human tooltip (the Objects picker's chip title). An asset's leads with its size, HP and armor. */
function objectProfileSummary(pick: ObjectPick): string {
  const asset = isMapAssetId(pick) ? mapAsset(pick)! : undefined;
  const { blocksMovement, blocksSight, blocksFlight, cover, moveCost, rooted, remains } = isMapAssetId(pick) ? mapAsset(pick)!.objectProfile : OBJECT_PRESETS[pick].objectProfile;
  const bits: string[] = [];
  if (asset) bits.push(`Size ${asset.size}${asset.sections ? " per section" : ""}, ${asset.size * 10} HP, Armor ${asset.armor}`);
  if (blocksMovement) bits.push("blocks movement");
  if (moveCost !== undefined) bits.push(`difficult (${moveCost})`);
  if (blocksSight) bits.push("blocks sight");
  if (blocksFlight) bits.push("blocks flight");
  if (cover && cover !== "none") bits.push(`${cover} cover`);
  if (rooted) bits.push("can't be moved");
  if (remains) bits.push(`leaves ${TERRAIN_TILES[remains].name.toLowerCase()}`);
  return bits.length > 0 ? bits.join(", ") : "no spatial contribution";
}

/** One preset chip: picking it pre-fills the steppers to the pick's defaults. */
function PickChip({ pick, ctx }: { pick: ObjectPick; ctx: RailContext }) {
  const { toolbar: t, disabled } = ctx.build;
  return (
    <button
      type="button"
      className={`cw-chip${t.objectPreset === pick ? " cw-chip-on" : ""}`}
      disabled={disabled}
      aria-pressed={t.objectPreset === pick}
      title={objectProfileSummary(pick)}
      onClick={() => {
        const d = pickDefaults(pick);
        t.setObjectPreset(pick);
        t.setObjectSectionCount(d.sectionCount);
        t.setObjectArmor(d.armor);
      }}
    >
      {pickLabel(pick)}
    </button>
  );
}

/** A row of mutually-exclusive chips, one pressed. */
function Chips<T extends string>({ label, aria, values, value, disabled, titles, onPick }: { label: string; aria: string; values: readonly T[]; value: T; disabled: boolean; titles?: Partial<Record<T, string>>; onPick: (v: T) => void }) {
  return (
    <div className="cw-subtoggle" role="group" aria-label={aria}>
      <span className="cw-subtoggle-label">{label}</span>
      {values.map((v) => (
        <button key={v} type="button" className={`cw-chip${value === v ? " cw-chip-on" : ""}`} disabled={disabled} aria-pressed={value === v} title={titles?.[v]} onClick={() => onPick(v)}>
          {v}
        </button>
      ))}
    </div>
  );
}

/** Wall: full or low height, and a low wall's cover grade (a full wall blocks sight outright). */
function WallOptions({ ctx }: { ctx: RailContext }) {
  const { toolbar: t, disabled } = ctx.build;
  return (
    <>
      <Chips
        label="Height"
        aria="wall height"
        values={["full", "low"] as WallHeight[]}
        value={t.wallHeight}
        disabled={disabled}
        titles={{ full: "Blocks movement, sight, and flight", low: "Blocks ground movement only — flyable, sight passes" }}
        onPick={t.setWallHeight}
      />
      {t.wallHeight === "low" && (
        <Chips
          label="Wall cover"
          aria="wall cover grade"
          values={["soft", "hard"] as CoverGrade[]}
          value={t.wallCoverGrade}
          disabled={disabled}
          titles={{ soft: "+1 Difficulty on ranged attacks against a target behind this wall", hard: "+2 Difficulty on ranged attacks against a target behind this wall" }}
          onPick={t.setWallCoverGrade}
        />
      )}
      <div className="cw-hint">
        Drag vertex to vertex to lay a {t.wallHeight}
        {t.wallHeight === "low" ? ` (${t.wallCoverGrade} cover)` : ""} wall. X or Alt = erase.
      </div>
    </>
  );
}

/** Room: auto-wall walls a committed room's perimeter and clears its interior, in one undo step. */
function RoomOptions({ ctx }: { ctx: RailContext }) {
  const { toolbar: t, disabled } = ctx.build;
  return (
    <>
      <Chips
        label="Auto-wall"
        aria="auto-wall"
        values={["on", "off"] as const}
        value={t.autoWall ? "on" : "off"}
        disabled={disabled}
        titles={{ on: "Wall a room's perimeter (and clear its interior) on commit — same undo step" }}
        onPick={(v) => t.setAutoWall(v === "on")}
      />
      <div className="cw-hint">Drag a box to author a room (start inside a room to extend it). X or Alt = erase.</div>
    </>
  );
}

/** Objects: preset or map asset, height, sections, armor, orientation, and the placed groups with a delete each. */
function ObjectOptions({ ctx }: { ctx: RailContext }) {
  const { toolbar: t, disabled, state, actorId, dispatch } = ctx.build;
  const groups = useMemo(() => listObjectGroups(state), [state]);
  const remove = (g: ObjectGroupSummary) => dispatch(placedObjectRemoveCommand(actorId, g));
  const isAsset = isMapAssetId(t.objectPreset);
  const sectionRange = pickSectionRange(t.objectPreset);
  const sections = sectionRange !== null && t.objectSectionCount > 1 ? ` (${t.objectSectionCount} sections)` : "";
  return (
    <>
      <div className="cw-subtoggle" role="group" aria-label="object preset">
        <span className="cw-subtoggle-label">Preset</span>
        {OBJECT_PRESET_LIST.map((p) => (
          <PickChip key={p} pick={p} ctx={ctx} />
        ))}
      </div>
      <div className="cw-subtoggle cw-subtoggle-wrap" role="group" aria-label="map asset">
        <span className="cw-subtoggle-label">Assets</span>
        {ASSET_PRESET_LIST.map((p) => (
          <PickChip key={p} pick={p} ctx={ctx} />
        ))}
      </div>
      {presetHasHeight(t.objectPreset) && (
        <Chips
          label="Height"
          aria="object wall height"
          values={["full", "low"] as WallHeight[]}
          value={t.objectHeight}
          disabled={disabled}
          titles={{ full: "Blocks movement, sight, and flight; hard cover", low: "Blocks ground movement only — flyable, sight passes, soft cover" }}
          onPick={t.setObjectHeight}
        />
      )}
      {(sectionRange !== null || !isAsset) && (
        <div className="cw-dims">
          {sectionRange !== null && <Stepper label="Sections" value={t.objectSectionCount} min={sectionRange.min} max={sectionRange.max} disabled={disabled} onChange={t.setObjectSectionCount} />}
          {!isAsset && <Stepper label="Armor" value={t.objectArmor} min={MIN_ARMOR} max={MAX_ARMOR} disabled={disabled} onChange={t.setObjectArmor} />}
        </div>
      )}
      {sectionRange !== null && t.objectSectionCount > 1 && (
        <Chips label="Orientation" aria="object orientation" values={["horizontal", "vertical"] as ObjectOrientation[]} value={t.objectOrientation} disabled={disabled} onPick={t.setObjectOrientation} />
      )}
      <div className="cw-hint">
        Click the board to place a {presetHasHeight(t.objectPreset) ? `${t.objectHeight} ` : ""}
        {pickLabel(t.objectPreset)}
        {sections}.{" "}
        {isAsset
          ? mapAsset(t.objectPreset)!.blurb
          : "Objects are combatants: destroy a section in PLAY through the ordinary attack path. They don't ride scene/map presets yet."}
      </div>
      {groups.length > 0 && (
        <div className="cw-tool-section">
          <div className="cw-section-name">Placed objects</div>
          <ul className="cw-unit-list">
            {groups.map((g) => (
              <li key={g.groupId} className="cw-unit-row">
                <div className="cw-unit" style={{ cursor: "default" }}>
                  <span className="cw-unit-glyph">{nameGlyph(g.name)}</span>
                  <span className="cw-unit-name">{g.name}</span>
                  {g.memberIds.length > 1 && <span className="cw-unit-tag">{g.memberIds.length} sections</span>}
                </div>
                <button
                  type="button"
                  className="cw-unit-remove"
                  disabled={disabled}
                  aria-label={`delete ${g.name}`}
                  {...tipAttrs({ label: `Delete ${g.name}`, hint: `${g.memberIds.length} section${g.memberIds.length === 1 ? "" : "s"}` })}
                  onClick={() => remove(g)}
                >
                  <Icon icon={faTrashCan} />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

/**
 * Tile: the tile palette (`TilePalette`, rehosted), and the ADVANCED fold with the custom-cell
 * brushes (`{ tile: "custom", properties }`, `decisions/0185` point 5). The fold is shut by default
 * and forced open while one of its brushes is armed, since a number key can arm one with it shut.
 */
function TileOptions({ ctx }: { ctx: RailContext }) {
  const { toolbar: t, disabled, state } = ctx.build;
  const brush = disabled ? null : t.brush;
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const mechanicArmed = isMechanicBrush(brush);
  const shown = advancedOpen || mechanicArmed;
  const toggleAdvanced = () => {
    if (!shown) return setAdvancedOpen(true);
    setAdvancedOpen(false);
    // Hiding the armed custom brush hands the Tile tool back to the palette's tile.
    if (mechanicArmed) t.pickTile(t.tile);
  };
  const tileName = TERRAIN_TILES[t.tile].name;
  const hint =
    brush === "tile"
      ? t.tile === OPEN_GROUND_TILE
        ? "Drag a box to clear to open ground; click for one cell. Shift = freehand."
        : `Drag a box to paint ${tileName}; click for one cell. Painting ${tileName} over ${tileName} clears it. Shift = freehand, X or Alt = erase.`
      : brush === "dangerous"
        ? "Drag a box to fill; click for one cell. ENGINEERING check or 5 dmg on entry/end-of-turn. Shift = freehand, X or Alt = erase."
        : "Drag a box to fill; click for one cell. Shift = freehand, X or Alt = erase.";
  return (
    <>
      <TilePalette armed={brush === "tile" ? t.tile : null} disabled={disabled} ground={encounterGround(state)} onPick={t.pickTile} onDisarm={() => t.pickBrush(null)} />
      <section className="cw-tool-section cw-advanced">
        <button type="button" className="cw-advanced-toggle" aria-expanded={shown} aria-controls="cw-advanced-brushes" onClick={toggleAdvanced}>
          <span aria-hidden="true">{shown ? "▾" : "▸"}</span> Advanced: custom cells
        </button>
        {shown && (
          <div id="cw-advanced-brushes" className="cw-advanced-body">
            <div className="cw-rail">
              {ADVANCED_BRUSHES.map((b) => {
                const on = brush === b.id;
                return (
                  <button
                    key={b.id}
                    type="button"
                    className={`cw-brush cw-rail-btn${on ? " cw-brush-active" : ""}`}
                    data-brush={b.id}
                    disabled={disabled}
                    aria-pressed={on}
                    title={`${b.label} (${b.key})`}
                    onClick={() => (on ? t.pickTile(t.tile) : t.pickBrush(b.id))}
                  >
                    <span className="cw-rail-key">{b.key}</span>
                    <span className="cw-rail-label">{b.label}</span>
                  </button>
                );
              })}
            </div>
            {brush === "cover" && <Chips label="Cover" aria="cover grade" values={["soft", "hard"] as CoverGrade[]} value={t.coverGrade} disabled={disabled} onPick={t.setCoverGrade} />}
            {brush === "dangerous" && <Chips label="Hazard" aria="hazard type" values={HAZARD_TYPES} value={t.hazard} disabled={disabled} onPick={t.setHazard} />}
            <div className="cw-hint">These paint one-off cells by mechanic, which hover reads as &ldquo;Custom terrain&rdquo;. Prefer a tile when one fits.</div>
          </div>
        )}
      </section>
      <div className="cw-hint">{hint}</div>
    </>
  );
}

/** A pointer-tool entry (Wall, Room, Objects): armed while `tool` is `id`; pressing it again returns to Select. */
function pointerTool(args: Pick<RailTool, "label" | "icon" | "key" | "hint" | "Options"> & { id: "wall" | "room" | "object" }): RailTool {
  const { id, ...rest } = args;
  return {
    ...rest,
    id,
    section: "build",
    modes: ["edit"],
    optionsAs: "panel",
    isArmed: (ctx) => ctx.build.toolbar.tool === id,
    press: (ctx) => ctx.build.toolbar.pickTool(ctx.build.toolbar.tool === id ? "select" : id),
  };
}

/** The BUILD section's tools, top to bottom. */
export const BUILD_TOOLS: readonly RailTool[] = [
  pointerTool({ id: "wall", label: "Wall", icon: faTrowelBricks, key: "6", hint: "lay a wall: drag from corner to corner", Options: WallOptions }),
  pointerTool({ id: "room", label: "Room", icon: faDoorOpen, key: "7", hint: "make a room: drag a box", Options: RoomOptions }),
  pointerTool({ id: "object", label: "Objects", icon: faCubes, key: "8", hint: "place a wall section, boulder or smoke: click a cell", Options: ObjectOptions }),
  {
    id: "tile",
    label: "Tile",
    icon: faTableCells,
    key: "t",
    hint: "paint terrain tiles from the palette; custom cells under ADVANCED (2–5, 9)",
    section: "build",
    modes: ["edit"],
    optionsAs: "panel",
    // Any brush is the Tile tool's: the palette's tile or an ADVANCED custom-cell brush.
    isArmed: (ctx) => ctx.build.toolbar.brush !== null,
    press: (ctx) => {
      const t = ctx.build.toolbar;
      if (t.brush !== null) t.pickBrush(null);
      else t.pickTile(t.tile);
    },
    Options: TileOptions,
  },
  {
    id: "erase-direction",
    label: "Erase direction",
    icon: faDeleteLeft,
    key: "x",
    hint: "switch painting, walls and rooms to erasing (or hold Alt)",
    section: "build",
    modes: ["edit"],
    isArmed: (ctx) => ctx.build.toolbar.eraseMode,
    press: (ctx) => ctx.build.toolbar.setEraseMode((v) => !v),
  },
];

/** A tiny inline −/+ numeric stepper (the Objects panel's Sections/Armor pickers); every change applies at once. */
function Stepper({ label, value, min, max, disabled, onChange }: { label: string; value: number; min: number; max: number; disabled: boolean; onChange: (n: number) => void }) {
  const clamp = (n: number) => Math.max(min, Math.min(max, Math.round(n)));
  return (
    <div className="cw-dim">
      <span className="cw-dim-label">{label}</span>
      <button type="button" className="cw-step" disabled={disabled || value <= min} aria-label={`decrease ${label}`} {...tipAttrs({ label: `${label} −1` })} onClick={() => onChange(clamp(value - 1))}>
        −
      </button>
      <input
        className="cw-dim-input"
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        value={value}
        disabled={disabled}
        onChange={(e) => {
          const n = Number(e.target.value);
          if (Number.isFinite(n)) onChange(clamp(n));
        }}
      />
      <button type="button" className="cw-step" disabled={disabled || value >= max} aria-label={`increase ${label}`} {...tipAttrs({ label: `${label} +1` })} onClick={() => onChange(clamp(value + 1))}>
        +
      </button>
    </div>
  );
}
